import useMileage, {ChallengeType, MileageBoundary} from '@di-shared/features/mileage/useMileage';
import {
  MileageChallengeDesc,
  MileageChallengeDescError,
  MileageChallengeDescLoading,
  MileageChallengeDescUpdate,
} from '@di-shared/features/mileage/ecomileage/MileageChallengeDesc';
import {useState} from 'react';
import {getDDayString} from '@di-shared/utils/dDay';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {
  MileageChallenge20KGraph,
  MileageChallenge30KGraph,
  MileageChallenge50KGraph,
  MileageChallenge70KGraph,
  MileageChallengeErrorGraph,
  MileageChallengeFailGraph,
  MileageChallengeLoadingGraph,
  MileageChallengeTab,
  MileageChallengeUpdateGraph,
} from '@di-shared/features/mileage/ecomileage/MileageChallengeGraph';
import clsx from 'clsx';
import useOdometer from '@di-shared/features/mileage/useOdometer';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import MidwayPerformanceRegistration from '@di-shared/features/mileage/ecomileage/MidwayPerformanceRegistration';

interface MileageChallengeProps {
  vin: string;
}

function MileageChallenge({vin}: MileageChallengeProps) {
  const {getChallengeType, challengeDDay, challengeStartDate, challengeEndDate, challengeBoundary} = useMileage(vin);
  const {odometer, updatedDate} = useOdometer(vin);
  const challengeType = getChallengeType(odometer);

  switch (challengeType) {
    case ChallengeType.SEVEN:
      return (
        <MileageChallenge70K
          vin={vin}
          challengeStartDate={challengeStartDate}
          challengeEndDate={challengeEndDate}
          challengeBoundary={challengeBoundary}
          odometer={odometer}
          challengeDDay={challengeDDay}
          updatedDate={updatedDate}
        />
      );
    case ChallengeType.FIVE:
      return (
        <MileageChallenge50K
          vin={vin}
          challengeStartDate={challengeStartDate}
          challengeEndDate={challengeEndDate}
          challengeBoundary={challengeBoundary}
          odometer={odometer}
          challengeDDay={challengeDDay}
          updatedDate={updatedDate}
        />
      );
    case ChallengeType.TRREE:
      return (
        <MileageChallenge30K
          vin={vin}
          challengeStartDate={challengeStartDate}
          challengeEndDate={challengeEndDate}
          challengeBoundary={challengeBoundary}
          odometer={odometer}
          challengeDDay={challengeDDay}
          updatedDate={updatedDate}
        />
      );
    case ChallengeType.TWO:
      return (
        <MileageChallenge20K
          vin={vin}
          challengeStartDate={challengeStartDate}
          challengeEndDate={challengeEndDate}
          challengeBoundary={challengeBoundary}
          odometer={odometer}
          challengeDDay={challengeDDay}
          updatedDate={updatedDate}
        />
      );

    case ChallengeType.CHALLENGE_UNAVAILABLE:
      return (
        <MileageChallengeFail
          vin={vin}
          challengeStartDate={challengeStartDate}
          challengeEndDate={challengeEndDate}
          challengeBoundary={challengeBoundary}
          odometer={odometer}
          challengeDDay={challengeDDay}
          updatedDate={updatedDate}
        />
      );
    case ChallengeType.UPDATE_STATUS:
      return (
        <MileageChallengeUpdate
          vin={vin}
          challengeStartDate={challengeStartDate}
          challengeEndDate={challengeEndDate}
          odometer={odometer}
          challengeDDay={challengeDDay}
          updatedDate={updatedDate}
        />
      );
    default:
      throw new Error();
  }
}

export default MileageChallenge;

export interface MileageChallengeBaseProps {
  vin: string;
  challengeStartDate: Date;
  challengeEndDate: Date;
  challengeBoundary: MileageBoundary;
  odometer: number;
  challengeDDay: number;
  updatedDate: Date | null;
}

function getLimitDistanceByTab(challengeBoundary: MileageBoundary, mileageChallengeTab: MileageChallengeTab) {
  switch (mileageChallengeTab) {
    case MileageChallengeTab['70K']:
      return challengeBoundary.seven;
    case MileageChallengeTab['50K']:
      return challengeBoundary.five;
    case MileageChallengeTab['30K']:
      return challengeBoundary.three;
    case MileageChallengeTab['20K']:
      return challengeBoundary.two;
    case MileageChallengeTab['10K']:
    default:
      return challengeBoundary.persistent;
  }
}

function MileageChallenge70K({
  vin,
  challengeStartDate,
  challengeEndDate,
  challengeBoundary,
  odometer,
  challengeDDay,
  updatedDate,
}: MileageChallengeBaseProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [mileageChallengeTab, setMileageChallengeTab] = useState(MileageChallengeTab['70K']);
  const chips = [
    {text: t('T10125_$mileage', {mileage: 7}), tab: MileageChallengeTab['70K']},
    {
      text: t('T10125_$mileage', {mileage: 5}),
      tab: MileageChallengeTab['50K'],
    },
    {text: t('T10125_$mileage', {mileage: 3}), tab: MileageChallengeTab['30K']},
    {text: t('T10125_$mileage', {mileage: 2}), tab: MileageChallengeTab['20K']},
  ];
  return (
    <>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30228_$mileage', {mileage: 7})}
          <span className="badge badge_basic_container badge_25px">{getDDayString(challengeDDay)}</span>
        </h3>
        <p className="section_heading_desc">
          {t('T30230_$startDate_$endDate', {
            startDate: CldrDatetimeFormatter.format(challengeStartDate, 'yMMMd'),
            endDate: CldrDatetimeFormatter.format(challengeEndDate, 'yMMMd'),
          })}
        </p>
      </div>
      <div
        className="chips"
        role="tablist"
      >
        {chips.map(({text, tab}) => (
          <button
            key={text}
            type="button"
            className={clsx('chip', {'is-selected': mileageChallengeTab === tab})}
            role="tab"
            aria-selected={mileageChallengeTab === tab}
            onClick={() => setMileageChallengeTab(tab)}
          >
            <span className="body_small_r">{text}</span>
          </button>
        ))}
      </div>
      <MileageChallenge70KGraph
        odometer={odometer}
        limitDistance={challengeBoundary.seven}
        previousLimitDistance={0}
        tablimitDistance={getLimitDistanceByTab(challengeBoundary, mileageChallengeTab)}
        mileageChallengeTab={mileageChallengeTab}
      />
      <MileageChallengeDesc
        odometer={odometer}
        limitDistance={getLimitDistanceByTab(challengeBoundary, mileageChallengeTab)}
        challengeEndDate={challengeEndDate}
        mileageChallengeTab={mileageChallengeTab}
        updatedDate={updatedDate}
      />
    </>
  );
}

function MileageChallenge50K({
  challengeStartDate,
  challengeEndDate,
  challengeBoundary,
  odometer,
  challengeDDay,
  updatedDate,
}: MileageChallengeBaseProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [mileageChallengeTab, setMileageChallengeTab] = useState(MileageChallengeTab['50K']);
  const chips = [
    {
      text: t('T10125_$mileage', {mileage: 5}),
      tab: MileageChallengeTab['50K'],
    },
    {text: t('T10125_$mileage', {mileage: 3}), tab: MileageChallengeTab['30K']},
    {text: t('T10125_$mileage', {mileage: 2}), tab: MileageChallengeTab['20K']},
    {text: t('T10125_$mileage', {mileage: 7}), tab: MileageChallengeTab['70K']},
  ];
  return (
    <>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30228_$mileage', {mileage: 5})}
          <span className="badge badge_basic_container badge_25px">{getDDayString(challengeDDay)}</span>
        </h3>
        <p className="section_heading_desc">
          {t('T30230_$startDate_$endDate', {
            startDate: CldrDatetimeFormatter.format(challengeStartDate, 'yMMMd'),
            endDate: CldrDatetimeFormatter.format(challengeEndDate, 'yMMMd'),
          })}
        </p>
      </div>
      <div
        className="chips"
        role="tablist"
      >
        {chips.map(({text, tab}) => (
          <button
            key={text}
            type="button"
            className={clsx('chip', {'is-selected': mileageChallengeTab === tab})}
            role="tab"
            aria-selected={mileageChallengeTab === tab}
            onClick={() => setMileageChallengeTab(tab)}
          >
            <span className="body_small_r">{text}</span>
          </button>
        ))}
      </div>
      <MileageChallenge50KGraph
        odometer={odometer}
        limitDistance={challengeBoundary.five}
        previousLimitDistance={challengeBoundary.seven}
        tablimitDistance={getLimitDistanceByTab(challengeBoundary, mileageChallengeTab)}
        mileageChallengeTab={mileageChallengeTab}
      />
      <MileageChallengeDesc
        odometer={odometer}
        limitDistance={getLimitDistanceByTab(challengeBoundary, mileageChallengeTab)}
        challengeEndDate={challengeEndDate}
        mileageChallengeTab={mileageChallengeTab}
        updatedDate={updatedDate}
      />
    </>
  );
}

function MileageChallenge30K({
  challengeStartDate,
  challengeEndDate,
  challengeBoundary,
  odometer,
  challengeDDay,
  updatedDate,
}: MileageChallengeBaseProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [mileageChallengeTab, setMileageChallengeTab] = useState(MileageChallengeTab['30K']);
  const chips = [
    {text: t('T10125_$mileage', {mileage: 3}), tab: MileageChallengeTab['30K']},
    {text: t('T10125_$mileage', {mileage: 2}), tab: MileageChallengeTab['20K']},
    {text: t('T10125_$mileage', {mileage: 7}), tab: MileageChallengeTab['70K']},
    {
      text: t('T10125_$mileage', {mileage: 5}),
      tab: MileageChallengeTab['50K'],
    },
  ];
  return (
    <>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30228_$mileage', {mileage: 3})}
          <span className="badge badge_basic_container badge_25px">{getDDayString(challengeDDay)}</span>
        </h3>
        <p className="section_heading_desc">
          {t('T30230_$startDate_$endDate', {
            startDate: CldrDatetimeFormatter.format(challengeStartDate, 'yMMMd'),
            endDate: CldrDatetimeFormatter.format(challengeEndDate, 'yMMMd'),
          })}
        </p>
      </div>
      <div
        className="chips"
        role="tablist"
      >
        {chips.map(({text, tab}) => (
          <button
            key={text}
            type="button"
            className={clsx('chip', {'is-selected': mileageChallengeTab === tab})}
            role="tab"
            aria-selected={mileageChallengeTab === tab}
            onClick={() => setMileageChallengeTab(tab)}
          >
            <span className="body_small_r">{text}</span>
          </button>
        ))}
      </div>
      <MileageChallenge30KGraph
        odometer={odometer}
        limitDistance={challengeBoundary.three}
        previousLimitDistance={challengeBoundary.five}
        tablimitDistance={getLimitDistanceByTab(challengeBoundary, mileageChallengeTab)}
        mileageChallengeTab={mileageChallengeTab}
      />
      <MileageChallengeDesc
        odometer={odometer}
        limitDistance={getLimitDistanceByTab(challengeBoundary, mileageChallengeTab)}
        challengeEndDate={challengeEndDate}
        mileageChallengeTab={mileageChallengeTab}
        updatedDate={updatedDate}
      />
    </>
  );
}

function MileageChallenge20K({
  challengeStartDate,
  challengeEndDate,
  challengeBoundary,
  odometer,
  challengeDDay,
  updatedDate,
}: MileageChallengeBaseProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [mileageChallengeTab, setMileageChallengeTab] = useState(MileageChallengeTab['20K']);
  const chips = [
    {text: t('T10125_$mileage', {mileage: 2}), tab: MileageChallengeTab['20K']},
    {text: t('T10125_$mileage', {mileage: 7}), tab: MileageChallengeTab['70K']},
    {
      text: t('T10125_$mileage', {mileage: 5}),
      tab: MileageChallengeTab['50K'],
    },
    {text: t('T10125_$mileage', {mileage: 3}), tab: MileageChallengeTab['30K']},
  ];
  return (
    <>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30228_$mileage', {mileage: 2})}
          <span className="badge badge_basic_container badge_25px">{getDDayString(challengeDDay)}</span>
        </h3>
        <p className="section_heading_desc">
          {t('T30230_$startDate_$endDate', {
            startDate: CldrDatetimeFormatter.format(challengeStartDate, 'yMMMd'),
            endDate: CldrDatetimeFormatter.format(challengeEndDate, 'yMMMd'),
          })}
        </p>
      </div>
      <div
        className="chips"
        role="tablist"
      >
        {chips.map(({text, tab}) => (
          <button
            key={text}
            type="button"
            className={clsx('chip', {'is-selected': mileageChallengeTab === tab})}
            role="tab"
            aria-selected={mileageChallengeTab === tab}
            onClick={() => setMileageChallengeTab(tab)}
          >
            <span className="body_small_r">{text}</span>
          </button>
        ))}
      </div>
      <MileageChallenge20KGraph
        odometer={odometer}
        limitDistance={challengeBoundary.two}
        previousLimitDistance={challengeBoundary.three}
        tablimitDistance={getLimitDistanceByTab(challengeBoundary, mileageChallengeTab)}
        mileageChallengeTab={mileageChallengeTab}
      />
      <MileageChallengeDesc
        odometer={odometer}
        limitDistance={getLimitDistanceByTab(challengeBoundary, mileageChallengeTab)}
        challengeEndDate={challengeEndDate}
        mileageChallengeTab={mileageChallengeTab}
        updatedDate={updatedDate}
      />
    </>
  );
}

function MileageChallengeFail({
  challengeStartDate,
  challengeEndDate,
  challengeBoundary,
  odometer,
  challengeDDay,
  updatedDate,
}: MileageChallengeBaseProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [mileageChallengeTab, setMileageChallengeTab] = useState(MileageChallengeTab['70K']);
  const chips = [
    {text: t('T10125_$mileage', {mileage: 7}), tab: MileageChallengeTab['70K']},
    {
      text: t('T10125_$mileage', {mileage: 5}),
      tab: MileageChallengeTab['50K'],
    },
    {text: t('T10125_$mileage', {mileage: 3}), tab: MileageChallengeTab['30K']},
    {text: t('T10125_$mileage', {mileage: 2}), tab: MileageChallengeTab['20K']},
  ];
  return (
    <>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30229')}
          <span className="badge badge_25px badge_filled_container badge_container_gray">{getDDayString(challengeDDay)}</span>
        </h3>
        <p className="section_heading_desc">
          {t('T30230_$startDate_$endDate', {
            startDate: CldrDatetimeFormatter.format(challengeStartDate, 'yMMMd'),
            endDate: CldrDatetimeFormatter.format(challengeEndDate, 'yMMMd'),
          })}
        </p>
      </div>
      <div
        className="chips"
        role="tablist"
      >
        {chips.map(({text, tab}) => (
          <button
            key={text}
            type="button"
            className={clsx('chip', {'is-selected': mileageChallengeTab === tab})}
            role="tab"
            aria-selected={mileageChallengeTab === tab}
            onClick={() => setMileageChallengeTab(tab)}
          >
            <span className="body_small_r">{text}</span>
          </button>
        ))}
      </div>
      <MileageChallengeFailGraph mileageChallengeTab={mileageChallengeTab} />
      <MileageChallengeDesc
        odometer={odometer}
        limitDistance={getLimitDistanceByTab(challengeBoundary, mileageChallengeTab)}
        challengeEndDate={challengeEndDate}
        mileageChallengeTab={mileageChallengeTab}
        updatedDate={updatedDate}
      />
    </>
  );
}

function MileageChallengeUpdate({vin, odometer, challengeDDay, updatedDate}: Omit<MileageChallengeBaseProps, 'challengeBoundary'>) {
  const {t} = useInferableTranslation();
  return (
    <>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30247')}
          <span className="badge badge_basic_container badge_25px">{getDDayString(challengeDDay)}</span>
        </h3>
        <p className="section_heading_desc">
          {t('T30359')}:&nbsp;{t('T30360')}
        </p>
      </div>
      <MileageChallengeUpdateGraph />
      <MidwayPerformanceRegistration vin={vin} />
      <MileageChallengeDescUpdate
        odometer={odometer}
        updatedDate={updatedDate}
      />
    </>
  );
}

export function MileageChallengeError() {
  const {t} = useInferableTranslation();
  return (
    <>
      <div className="section_heading type_main">
        <h3 className="section_heading_title">{t('T30249')}</h3>
      </div>
      <MileageChallengeErrorGraph />
      <MileageChallengeDescError />
    </>
  );
}

export function MileageChallengeOdometerError({vin}: MileageChallengeProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {challengeStartDate, challengeEndDate, challengeDDay} = useMileage(vin);
  return (
    <>
      <div className="section_heading type_main">
        <h3 className="section_heading_title">
          {t('T30249')}
          <span className="badge badge_basic_container badge_25px">{getDDayString(challengeDDay)}</span>
        </h3>
        <p className="section_heading_desc">
          {t('T30230_$startDate_$endDate', {
            startDate: CldrDatetimeFormatter.format(challengeStartDate, 'yMMMd'),
            endDate: CldrDatetimeFormatter.format(challengeEndDate, 'yMMMd'),
          })}
        </p>
      </div>
      <MileageChallengeErrorGraph />
      <MileageChallengeDescError />
    </>
  );
}

export function MileageChallengeLoading() {
  const {t} = useInferableTranslation();
  const chips = [
    {text: t('T10125_$mileage', {mileage: 7}), tab: MileageChallengeTab['70K']},
    {
      text: t('T10125_$mileage', {mileage: 5}),
      tab: MileageChallengeTab['50K'],
    },
    {text: t('T10125_$mileage', {mileage: 3}), tab: MileageChallengeTab['30K']},
    {text: t('T10125_$mileage', {mileage: 2}), tab: MileageChallengeTab['20K']},
  ];
  return (
    <>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title skeleton"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30247')}
          <span className="badge badge_basic_container badge_25px skeleton">D-</span>
        </h3>
        <p className="section_heading_desc skeleton">
          {t('T30230_$startDate_$endDate', {
            startDate: '',
            endDate: '',
          })}
        </p>
      </div>
      <div
        className="chips"
        role="tablist"
      >
        {chips.map(({text}) => (
          <button
            key={text}
            type="button"
            className={clsx('chip skeleton')}
            role="tab"
            aria-selected={false}
          >
            <span className="body_small_r">{text}</span>
          </button>
        ))}
      </div>
      <MileageChallengeLoadingGraph />
      <MileageChallengeDescLoading />
    </>
  );
}
